import type { SupportedLangs } from "@/i18n/types";

export const memberRoleList = [
  "org:super_admin",
  "org:admin",
  "org:editor",
  "org:viewer",
] as const;

export const memberRoleObjects = [
  { id: "org:super_admin", name: "(SuperAdmin)上位管理者" },
  { id: "org:admin", name: "(Admin)管理者" },
  { id: "org:editor", name: "(Editor)編集者" },
  { id: "org:viewer", name: "(Viewer)閲覧者" },
];

// サーバーから返ってくるメンバのデータ
export type MemberRaw = {
  org_id: string;
  uid: string;
  idp_uid: string;
  email: string;
  display_name: string;
  email_verified: boolean;
  use_mfa: boolean;
  identity_provider: string;
  picture_type: string;
  picture_color: string;
  picture_image_url: string;
  role: (typeof memberRoleList)[number];
  description: string;
  is_disabled: boolean;
  is_new_user: boolean;
  created_at: string;
  updated_at: string;
  is_aa_constituent: boolean;
};

// サーバーに投げるメンバのデータ
type MemberFormBase = {
  uid: string;
  idp_uid: string;
  is_new_user: boolean;
  email: string;
  role: string;
  description: string;
  display_name: string;
};

export type MemberAddForm = Pick<MemberFormBase, "email" | "role"> &
  Partial<Pick<MemberFormBase, "description" | "display_name">>;

export type MemberInvitationBody = MemberAddForm & {
  invitation_locale: SupportedLangs;
};

export type MemberAddResponse = {
  item: MemberRaw;
  user_existed: boolean;
};

export type MemberUpdateForm = Pick<
  MemberFormBase,
  "uid" | "idp_uid" | "is_new_user"
> &
  Partial<Pick<MemberFormBase, "description" | "role">>;

export const initMemberRaw: MemberRaw = {
  org_id: "",
  uid: "",
  idp_uid: "",
  email: "",
  display_name: "",
  email_verified: false,
  use_mfa: false,
  identity_provider: "",
  picture_type: "",
  picture_color: "",
  picture_image_url: "",
  // 初期Roleはorg:editor
  role: "org:editor",
  description: "",
  is_disabled: false,
  is_new_user: false,
  created_at: "",
  updated_at: "",
  is_aa_constituent: false,
};

// NOTE: 新規ユーザーはUIDを持っていないのでidp_uidを識別子として使う
// しかし、idp_uidはパイプが文字列に入っており、DOMのkeyとしては使えないので、その時は取り除いて利用する
export const getMemberUserId = ({
  member,
  isParsedIdpUid,
}: {
  member: MemberRaw | MemberUpdateForm;
  isParsedIdpUid: boolean;
}) =>
  !member.is_new_user
    ? member.uid
    : isParsedIdpUid
      ? member.idp_uid.split("|")[1]
      : member.idp_uid;

/**
 * メンバの編集権限があるユーザーについて、対象のロールが編集可能かどうかを取得する
 * @param isAdminMode 管理画面かどうか
 * @param myRole 自分のロール
 * @param targetRole 対象のロール
 * @returns 対象のロールが編集可能かどうか
 */
export const getIsEditableRole = ({
  isAdminMode,
  myRole,
  targetRole,
}: {
  isAdminMode: boolean;
  myRole: string | undefined;
  targetRole: string;
}) => {
  // 管理画面またはSuperAdminの場合はすべてのロールが編集可能
  // それ以外の場合は下記の変更が不可
  // - SuperAdminからそれ以外のロールへの変更
  // - それ以外のロール -> SuperAdminへの変更
  return (
    isAdminMode ||
    myRole === "org:super_admin" ||
    targetRole !== "org:super_admin"
  );
};
